import { useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { sanitizeEmailStatus } from 'components/SelectEmailStatus'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader, ContentBody } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { getDateRangeV2, sanitizeDateRange } from 'features/App/components/Filters/DateRangeFilter'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { BookingsDashboard } from 'features/Bookings/components/Dashboard'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { BOOKINGS_LIMIT } from 'features/Bookings/constants'
import { GET_BOOKINGS } from 'features/Bookings/queries'
import { resolvers as bookingsResolvers } from 'resolvers/bookings'
import { getDateRangeQuery } from 'utils/date'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead, canShowBookingsDashboard } from 'features/Users/utils'
import { BookingsList } from './List'
import { BookingsPagination } from './Pagination'
import { BookingFilters } from './Filters'
import { subMonths } from 'date-fns'
import { usePlace } from 'features/Places/hooks'
import { useLogin } from 'features/Login/hooks'
import { useSiteAutocomplete } from 'features/Sites/hooks'
import { useSalesChannelAutocomplete } from 'features/Services/hooks'
import { useCompanyLineAutocomplete } from 'features/Lines/hooks'
import { GTRouteServiceSuggestionsResponse } from 'gql/types.generated'
import { GET_ROUTE_SERVICES_AUTOCOMPLETE } from 'features/Services/queries-legacy'
import { getShortId } from 'utils/string'
import { createBookingCollection } from 'features/Bookings/collections'
import { BookingsV2BuyerEmailStatusInput, BookingV2Status } from 'gql/generated/graphql'

const messages = defineMessages({
  helmet: {
    id: 'bookings.helmet',
    defaultMessage: 'Bookings | Nexbus Admin'
  }
})

const BookingsView = (): ReactElement => {
  let defaultSortingDateFrom, defaultSortingDateTo
  const location = useLocation()
  const intl = useIntl()
  const client = useApolloClient()
  const login = useLogin()
  const isAuthorized = canRead(FEATURES.BOOKING, login.accessToken.policies)

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content
  // @ts-expect-error FIXME
  client.addResolvers(bookingsResolvers)
  const {
    page = '1',
    status = '',
    reservations = '',
    salesChannelId = '',
    email = '',
    origin = '',
    destination = '',
    purchaseDateFrom,
    purchaseDateTo,
    purchaseDateRangeValue,
    travelDateFrom,
    travelDateTo,
    travelDateRangeValue,
    sortingDateRangeValue,
    sortingDateFrom,
    sortingDateTo,
    site = '',
    buyerPhone = '',
    emailStatus = '',
    companyLineId = '',
    routeServiceIds = '',
    coupon = ''
  } = getQueries(location.search)

  const sanitizedPurchaseDateRange = sanitizeDateRange({
    from: purchaseDateFrom,
    to: purchaseDateTo,
    rangeValue: purchaseDateRangeValue
  })
  const sanitizedTravelDateRange = sanitizeDateRange({
    from: travelDateFrom,
    to: travelDateTo,
    rangeValue: travelDateRangeValue
  })
  if (!(sortingDateFrom || sortingDateTo || sortingDateRangeValue)) {
    const now = new Date()
    const { from, to } = getDateRangeQuery({
      rangeValue: '',
      from: subMonths(now, 4),
      to: now
    })
    defaultSortingDateFrom = from
    defaultSortingDateTo = to
  }
  const sanitizedSortingDateRange = sanitizeDateRange({
    from: defaultSortingDateFrom || sortingDateFrom,
    to: defaultSortingDateTo || sortingDateTo,
    rangeValue: sortingDateRangeValue
  })
  const statusList = status ? status.split(',') : []
  const reservationsList = reservations ? reservations.split(',') : []
  const routeServiceIdsList = routeServiceIds ? routeServiceIds.split(',') : []
  const pageNb = parseInt(page)

  const originPlace = usePlace(origin, { skip: !origin })
  const destinationPlace = usePlace(destination, { skip: !destination })
  const siteData = useSiteAutocomplete(site, { skip: !site })
  const salesChannel = useSalesChannelAutocomplete(salesChannelId, { skip: !salesChannelId })
  const companyLine = useCompanyLineAutocomplete(companyLineId, { skip: !companyLineId })
  const { data: routeServicesData } = useQuery<{ routeServiceSuggestions: GTRouteServiceSuggestionsResponse }>(
    GET_ROUTE_SERVICES_AUTOCOMPLETE,
    {
      variables: {
        input: {
          ids: routeServiceIdsList
        }
      },
      skip: isEmpty(routeServiceIdsList)
    }
  )
  const { loading, error, data, refetch, networkStatus } = useQuery(GET_BOOKINGS, {
    skip: !isAuthorized,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        page: pageNb,
        size: BOOKINGS_LIMIT
      },
      input: filterEmptyValue({
        salesChannelID: salesChannelId,
        statuses: statusList as BookingV2Status[],
        buyerEmail: email,
        buyerEmailStatus: emailStatus as BookingsV2BuyerEmailStatusInput,
        shortIDs: reservationsList.map((reservation) => getShortId(reservation)),
        originID: origin,
        destinationID: destination,
        routeServiceIDs: routeServiceIdsList,
        siteID: site,
        buyerPhone,
        companyLineID: companyLineId,
        createdAtRange: getDateRangeV2(sanitizedSortingDateRange),
        departureAtRange: getDateRangeV2(sanitizedTravelDateRange),
        confirmedAtRange: getDateRangeV2(sanitizedPurchaseDateRange),
        couponCode: coupon
      })
    }
  })

  const filters = {
    salesChannel: {
      id: salesChannel?.id || '',
      name: salesChannel?.name || ''
    },
    status: statusList,
    emailStatus: sanitizeEmailStatus(emailStatus),
    travelDateRange: sanitizedTravelDateRange,
    email,
    reservations: reservationsList,
    purchaseDateRange: sanitizedPurchaseDateRange,
    sortingDateRange: sanitizedSortingDateRange,
    routeServices: routeServicesData?.routeServiceSuggestions.items || [],
    origin: { id: originPlace?.id || '', name: originPlace?.name || '' },
    destination: { id: destinationPlace?.id || '', name: destinationPlace?.name || '' },
    site: {
      id: siteData?.id || '',
      name: siteData?.name || ''
    },
    buyerPhone,
    companyLine: {
      id: companyLine?.id || '',
      name: companyLine?.name || ''
    },
    coupon
  }

  useEffect(() => {
    refetch()
  }, [])

  if (!isAuthorized) {
    return <ForbiddenView feature={FEATURES.BOOKING} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="bookings.error.description"
            defaultMessage="There was an error while loading your bookings"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="bookings.error.action" defaultMessage="Reload your bookings" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.bookingsV2.items) && pageNb === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="bookings.empty.description" defaultMessage="You don't have any bookings yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/routes" kind="primary">
            <FormattedMessage id="bookings.empty.action" defaultMessage="Add a service first" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.bookingsV2.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="bookings.empty.filters.description"
            defaultMessage="There was no booking found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/bookings" kind="primary">
            <FormattedMessage id="bookings.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <BookingsList bookingsCollection={createBookingCollection(data?.bookingsV2.items || [])} />
        <BookingsPagination
          page={pageNb}
          totalItems={data?.bookingsV2.total || 0}
          limit={BOOKINGS_LIMIT}
          currentItemsCount={data?.bookingsV2.items.length || 0}
          filters={filters}
        />
      </>
    )
  }

  return (
    <>
      {canShowBookingsDashboard(login.accessToken.policies) && <BookingsDashboard />}
      <Content>
        <ContentHeader>
          <Heading.H4>
            <FormattedMessage id="bookings.list.header.label" defaultMessage="Bookings list" />
          </Heading.H4>
        </ContentHeader>
        <ContentBody>
          <BookingFilters filters={filters} />
          <section>{content}</section>
        </ContentBody>
      </Content>
    </>
  )
}

export default withLastUrl(BookingsView)
